/* global google */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { customersDbApi } from '../utils/api';
import loadGoogleMaps from '../utils/googleMapsLoader';
import { MapPinIcon, AlertTriangleIcon, SearchIcon } from './icons/LucideIcons';
import { address2AfterPick, strayRoomInAddress2 } from '../utils/address2Room';

// Use the global Google Maps loader (already imported from utils/googleMapsLoader)
const loadGoogleMapsAPI = loadGoogleMaps;

const US_STATES = [
  { code: '', name: 'Select State...' },
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
  { code: 'DC', name: 'District of Columbia' },
];

const STORAGE_KEY = 'florist_frequent_addresses';

// Format an ISO 'YYYY-MM-DD' date as 'MM/DD/YYYY' for display.
const formatMDY = (d) => (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) ? `${d.slice(5, 7)}/${d.slice(8, 10)}/${d.slice(0, 4)}` : (d || '');

const RecipientSection = ({
  recipient,
  onRecipientChange,
  customer,
  customerLocalId,
  sameAsCustomer,
  validationErrors = {},
  onNotification,
  tabIndex = 20,
  fulfillmentType = 'delivery',
  // Edit order only: picking a new address drops the old Address 2, and a
  // room left in Address 2 on a non-hospital delivery gets a warning.
  isEditing = false,
  locationType,
  // 'forest': the New order form's Recipient step (6-column grid). Edit order
  // keeps the original layout.
  layout,
  // Forest: the delivery's location instructions sit beside the recipient
  // phone, as in the design (formData.delivery.locationInstructions).
  deliveryInstructions = '',
  onDeliveryInstructionsChange,
}) => {
  const [frequentAddresses, setFrequentAddresses] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [showAddresses, setShowAddresses] = useState(false);
  const [addressValidation, setAddressValidation] = useState({});
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const [businessPredictions, setBusinessPredictions] = useState([]);
  const [showBusinessPredictions, setShowBusinessPredictions] = useState(false);
  const [selectedBusinessIndex, setSelectedBusinessIndex] = useState(-1);
  const [businessSearch, setBusinessSearch] = useState('');
  const [addressPredictions, setAddressPredictions] = useState([]);
  const [showAddressPredictions, setShowAddressPredictions] = useState(false);
  const [selectedAddressIndex, setSelectedAddressIndex] = useState(-1);
  const [previousOrders, setPreviousOrders] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  // Address 2 that picking a new address dropped, so staff can put it back.
  const [clearedAddress2, setClearedAddress2] = useState(null);

  const address1Ref = useRef(null);
  const businessSearchRef = useRef(null);
  const addressPredictionsRef = useRef(null);
  const businessPredictionsRef = useRef(null);
  const businessDebounceRef = useRef(null);
  const addressDebounceRef = useRef(null);

  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      if (businessDebounceRef.current) clearTimeout(businessDebounceRef.current);
      if (addressDebounceRef.current) clearTimeout(addressDebounceRef.current);
    };
  }, []);

  // Load Google Maps API
  useEffect(() => {
    const initializeAPI = async () => {
      try {
        await loadGoogleMapsAPI();
        initializeGoogleMaps();
      } catch (error) {
        // Failed to load Google Maps API, continuing without address autocomplete
      }
    };

    initializeAPI();
  }, []);

  // Load frequent addresses from localStorage
  useEffect(() => {
    loadFrequentAddresses();
  }, []);

  // Handle "Same as Customer" functionality
  useEffect(() => {
    if (sameAsCustomer && customer) {
      onRecipientChange({
        ...recipient,
        firstName: customer.firstName,
        lastName: customer.lastName,
        phone: customer.phone,
      });
    }
  }, [sameAsCustomer, customer]);

  // Load the customer's previous ROSA orders (includes imported Hana history) and collapse
  // them into one card per recipient — showing the last arrangement + price point for
  // reference. Uses the local customer id (same source the Order History modal uses).
  useEffect(() => {
    let cancelled = false;
    const loadPreviousOrders = async () => {
      if (!customerLocalId) {
        setPreviousOrders([]);
        return;
      }
      setLoadingRecipients(true);
      try {
        const response = await customersDbApi.getOrders(customerLocalId, { page: 1, limit: 50 });
        const orders = (response && response.data && response.data.orders) || [];
        // Orders are newest-first; keep the most recent order per distinct recipient.
        const seen = new Set();
        const cards = [];
        for (const o of orders) {
          const first = o.recipientFirstName || '';
          const last = o.recipientLastName || '';
          const ad = o.addressDetail || {};
          if (!first && !last && !ad.address1) continue;
          const key = `${first}|${last}|${ad.address1 || ''}|${ad.zip || ''}`.toLowerCase();
          if (seen.has(key)) continue;
          seen.add(key);
          cards.push({
            firstName: first,
            lastName: last,
            businessName: '',
            address1: ad.address1 || '',
            address2: ad.address2 || '',
            city: ad.city || '',
            state: ad.state || '',
            zip: ad.zip || '',
            phone: o.recipientPhone || '',
            arrangement: (o.items || []).map((it) => it.name).filter(Boolean).join(', '),
            total: Number(o.total) || 0,
            date: formatMDY(o.deliveryDate || (o.createdAt ? String(o.createdAt).slice(0, 10) : '')),
            fulfillment: o.fulfillmentType || '',
            isHana: o.orderSource === 'hana_import',
          });
          if (cards.length >= 8) break;
        }
        if (!cancelled) setPreviousOrders(cards);
      } catch (error) {
        console.warn('Failed to load previous orders:', error);
        if (!cancelled) setPreviousOrders([]);
      } finally {
        if (!cancelled) setLoadingRecipients(false);
      }
    };

    loadPreviousOrders();
    return () => { cancelled = true; };
  }, [customerLocalId]);

  const initializeGoogleMaps = () => {
    try {
      setGoogleMapsLoaded(true);
    } catch (error) {
      // Failed to initialize Google Maps services
    }
  };

  const loadFrequentAddresses = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const addresses = JSON.parse(saved);
        setFrequentAddresses(addresses.slice(0, 5)); // Show top 5
      }
    } catch (error) {
      // Failed to load frequent addresses
    }
  };

  const saveToFrequentAddresses = (address) => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      let addresses = saved ? JSON.parse(saved) : [];

      // Check if address already exists
      const addressKey = `${address.address1}-${address.city}-${address.state}-${address.zip}`;
      const existingIndex = addresses.findIndex(
        (addr) =>
          `${addr.address1}-${addr.city}-${addr.state}-${addr.zip}` ===
          addressKey
      );

      if (existingIndex >= 0) {
        // Move to top and update count
        addresses[existingIndex].useCount =
          (addresses[existingIndex].useCount || 0) + 1;
        addresses.unshift(addresses.splice(existingIndex, 1)[0]);
      } else {
        // Add new address
        addresses.unshift({
          ...address,
          useCount: 1,
          lastUsed: new Date().toISOString(),
        });
      }

      // Keep only top 10 addresses
      addresses = addresses.slice(0, 10);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(addresses));
      setFrequentAddresses(addresses.slice(0, 5));
    } catch (error) {
      // Failed to save frequent address
    }
  };

  const validateZipCode = (zip) => {
    // US ZIP code patterns: 12345 or 12345-6789
    const zipRegex = /^\d{5}(-\d{4})?$/;
    return zipRegex.test(zip);
  };

  const validateAddress = useCallback((address) => {
    const warnings = [];

    if (address.address1 && address.city && address.state && address.zip) {
      // Check for common address issues
      if (
        address.address1.toLowerCase().includes('po box') ||
        address.address1.toLowerCase().includes('p.o. box')
      ) {
        warnings.push(
          'PO Box addresses may not be suitable for flower delivery'
        );
      }

      if (!validateZipCode(address.zip)) {
        warnings.push('ZIP code format appears invalid');
      }

      // Check for apartment/suite without number
      const aptKeywords = ['apt', 'apartment', 'suite', 'ste', 'unit'];
      const hasAptKeyword = aptKeywords.some((keyword) =>
        address.address1.toLowerCase().includes(keyword)
      );

      if (hasAptKeyword && !address.address2) {
        warnings.push(
          'Consider adding apartment/suite number in Address Line 2'
        );
      }
    }

    return warnings;
  }, []);

  // Validate address whenever it changes
  useEffect(() => {
    if (
      recipient.address1 ||
      recipient.city ||
      recipient.state ||
      recipient.zip
    ) {
      const warnings = validateAddress(recipient);
      setAddressValidation({ warnings });
    } else {
      setAddressValidation({});
    }
  }, [
    recipient.address1,
    recipient.city,
    recipient.state,
    recipient.zip,
    validateAddress,
  ]);

  const handleInputChange = (field, value) => {
    if (field === 'address2') setClearedAddress2(null);
    const updatedRecipient = { ...recipient, [field]: value };
    onRecipientChange(updatedRecipient);

    // Save complete addresses to frequent addresses
    if (field === 'zip' && validateZipCode(value)) {
      const completeAddress = {
        firstName: updatedRecipient.firstName,
        lastName: updatedRecipient.lastName,
        businessName: updatedRecipient.businessName,
        address1: updatedRecipient.address1,
        address2: updatedRecipient.address2,
        city: updatedRecipient.city,
        state: updatedRecipient.state,
        zip: value,
        phone: updatedRecipient.phone,
      };

      if (
        completeAddress.address1 &&
        completeAddress.city &&
        completeAddress.state
      ) {
        saveToFrequentAddresses(completeAddress);
      }
    }
  };

  // eslint-disable-next-line no-unused-vars
  const selectFrequentAddress = (address) => {
    onRecipientChange({
      ...recipient,
      ...address,
    });
    setShowAddresses(false);
    onNotification?.('Address filled from frequent addresses', 'success', 2000);
  };

  // Smart detection to avoid API calls for obvious street addresses
  const looksLikeStreetAddress = (value) => {
    const addressPatterns = [
      /^\d+\s+.+/, // Starts with numbers (123 Main St)
      /^\d+[a-zA-Z]*\s+.+/, // Starts with numbers + letters (123A Oak Dr)
      /^(apt|apartment|unit|suite|ste|#)\s*/i, // Starts with apartment indicators
      /\b(st|street|ave|avenue|rd|road|dr|drive|ln|lane|way|blvd|boulevard|ct|court|pl|place|cir|circle|pkwy|parkway)\b/i, // Contains street suffixes
    ];
    return addressPatterns.some((pattern) => pattern.test(value.trim()));
  };

  // Business search function (debounced to reduce Places API calls)
  const handleBusinessSearch = (value) => {
    setBusinessSearch(value);

    if (!googleMapsLoaded || value.length < 2) {
      setBusinessPredictions([]);
      setShowBusinessPredictions(false);
      if (businessDebounceRef.current) clearTimeout(businessDebounceRef.current);
      return;
    }

    if (businessDebounceRef.current) clearTimeout(businessDebounceRef.current);
    businessDebounceRef.current = setTimeout(() => {
      try {
        if (!google?.maps?.places?.AutocompleteService) {
          console.warn('Places API not ready yet');
          return;
        }

        const service = new google.maps.places.AutocompleteService();

        const request = {
          input: value,
          types: ['establishment'],
          componentRestrictions: { country: 'us' },
        };

        service.getPlacePredictions(request, (results, status) => {
          if (
            status === google.maps.places.PlacesServiceStatus.OK &&
            results
          ) {
            setBusinessPredictions(results);
            setShowBusinessPredictions(true);
            setSelectedBusinessIndex(-1);
          } else {
            setBusinessPredictions([]);
            setShowBusinessPredictions(false);
          }
        });
      } catch (error) {
        setBusinessPredictions([]);
        setShowBusinessPredictions(false);
      }
    }, 300);
  };

  // Address field - shows autocomplete predictions (debounced to reduce Places API calls)
  const handleAddressChange = (value) => {
    handleInputChange('address1', value);

    if (!googleMapsLoaded || value.length < 3) {
      setAddressPredictions([]);
      setShowAddressPredictions(false);
      if (addressDebounceRef.current) clearTimeout(addressDebounceRef.current);
      return;
    }

    if (addressDebounceRef.current) clearTimeout(addressDebounceRef.current);
    addressDebounceRef.current = setTimeout(() => {
      try {
        if (!google?.maps?.places?.AutocompleteService) {
          console.warn('Places API not ready yet');
          return;
        }

        const service = new google.maps.places.AutocompleteService();

        const request = {
          input: value,
          types: ['address'],
          componentRestrictions: { country: 'us' },
        };

        service.getPlacePredictions(request, (results, status) => {
          if (
            status === google.maps.places.PlacesServiceStatus.OK &&
            results
          ) {
            setAddressPredictions(results);
            setShowAddressPredictions(true);
            setSelectedAddressIndex(-1);
          } else {
            setAddressPredictions([]);
            setShowAddressPredictions(false);
          }
        });
      } catch (error) {
        setAddressPredictions([]);
        setShowAddressPredictions(false);
      }
    }, 300);
  };

  // Edit order: a different street or zip drops the old Address 2 (a hospital
  // room shouldn't follow the recipient home) unless Google supplies a unit.
  const applyAddress2AfterPick = (updatedRecipient, unit) => {
    if (!isEditing) return;
    const { address2, cleared } = address2AfterPick(recipient, { ...updatedRecipient, unit });
    updatedRecipient.address2 = address2;
    setClearedAddress2(cleared);
  };

  const handleBusinessSelect = async (prediction) => {
    if (!googleMapsLoaded) return;

    try {
      const { Place } = await google.maps.importLibrary("places");
      const place = new Place({
        id: prediction.place_id,
        requestedLanguage: 'en-US',
      });

      await place.fetchFields({
        fields: ['displayName', 'formattedAddress', 'addressComponents', 'location', 'nationalPhoneNumber', 'internationalPhoneNumber']
      });

      // Extract address components using new API format
      const addressComponents = {};
      if (place.addressComponents) {
        place.addressComponents.forEach((component) => {
          const types = component.types;
          if (types.includes('street_number')) {
            addressComponents.street_number = component.longText;
          } else if (types.includes('route')) {
            addressComponents.route = component.longText;
          } else if (types.includes('locality')) {
            addressComponents.city = component.longText;
          } else if (types.includes('administrative_area_level_1')) {
            addressComponents.state = component.shortText;
          } else if (types.includes('postal_code')) {
            addressComponents.zip = component.longText;
          } else if (types.includes('subpremise')) {
            addressComponents.unit = component.longText;
          }
        });
      }

      // Build street address
      let streetAddress = '';
      if (addressComponents.street_number && addressComponents.route) {
        streetAddress = `${addressComponents.street_number} ${addressComponents.route}`;
      } else if (addressComponents.route) {
        streetAddress = addressComponents.route;
      } else if (place.displayName && place.displayName !== place.formattedAddress) {
        streetAddress = place.displayName;
      }

      const businessPhone = place.nationalPhoneNumber || place.internationalPhoneNumber;

      const updatedRecipient = {
        ...recipient,
        businessName: place.displayName || prediction.structured_formatting.main_text || '',
        address1: streetAddress || place.formattedAddress.split(',')[0],
        address2: addressComponents.unit || recipient.address2,
        city: addressComponents.city || '',
        state: addressComponents.state || recipient.state || '',
        zip: addressComponents.zip || '',
      };
      applyAddress2AfterPick(updatedRecipient, addressComponents.unit);

      // Surface the business phone as a visible alt phone with a "Business" context
      // so staff see it and can remove it with one click if Google has the wrong number.
      if (businessPhone) {
        updatedRecipient.alternativePhone = formatPhoneNumber(businessPhone);
        updatedRecipient.alternativePhoneContext = 'Business';
      }

      onRecipientChange(updatedRecipient);
      setBusinessPredictions([]);
      setShowBusinessPredictions(false);
      setBusinessSearch('');

      onNotification?.(
        businessPhone
          ? 'Address and business phone filled from search'
          : 'Address filled from business search',
        'success',
        3000
      );
    } catch (error) {
      onNotification?.(
        'Failed to get business details',
        'error',
        3000
      );
    }
  };

  const handleAddressSelect = async (prediction) => {
    if (!googleMapsLoaded) return;

    try {
      const { Place } = await google.maps.importLibrary("places");
      const place = new Place({
        id: prediction.place_id,
        requestedLanguage: 'en-US',
      });

      await place.fetchFields({
        fields: ['displayName', 'formattedAddress', 'addressComponents', 'location']
      });

      // Extract address components
      const addressComponents = {};
      if (place.addressComponents) {
        place.addressComponents.forEach((component) => {
          const types = component.types;
          if (types.includes('street_number')) {
            addressComponents.street_number = component.longText;
          } else if (types.includes('route')) {
            addressComponents.route = component.longText;
          } else if (types.includes('locality')) {
            addressComponents.city = component.longText;
          } else if (types.includes('administrative_area_level_1')) {
            addressComponents.state = component.shortText;
          } else if (types.includes('postal_code')) {
            addressComponents.zip = component.longText;
          } else if (types.includes('subpremise')) {
            addressComponents.unit = component.longText;
          }
        });
      }

      // Build street address
      let streetAddress = '';
      if (addressComponents.street_number && addressComponents.route) {
        streetAddress = `${addressComponents.street_number} ${addressComponents.route}`;
      } else if (addressComponents.route) {
        streetAddress = addressComponents.route;
      }

      // Update recipient data
      const updatedRecipient = {
        ...recipient,
        address1: streetAddress || place.formattedAddress.split(',')[0],
        address2: addressComponents.unit || recipient.address2,
        city: addressComponents.city || '',
        state: addressComponents.state || recipient.state || '',
        zip: addressComponents.zip || '',
      };
      applyAddress2AfterPick(updatedRecipient, addressComponents.unit);

      onRecipientChange(updatedRecipient);
      setAddressPredictions([]);
      setShowAddressPredictions(false);

      onNotification?.('Address filled from Google Maps', 'success', 2000);
    } catch (error) {
      onNotification?.(
        'Failed to get address details',
        'error',
        3000
      );
    }
  };

  const handleBusinessKeyDown = (e) => {
    if (!showBusinessPredictions || businessPredictions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedBusinessIndex((prev) =>
          prev < businessPredictions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedBusinessIndex((prev) =>
          prev > 0 ? prev - 1 : businessPredictions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedBusinessIndex >= 0) {
          handleBusinessSelect(businessPredictions[selectedBusinessIndex]);
        }
        break;
      case 'Escape':
        setBusinessPredictions([]);
        setShowBusinessPredictions(false);
        setSelectedBusinessIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleAddressKeyDown = (e) => {
    if (!showAddressPredictions || addressPredictions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedAddressIndex((prev) =>
          prev < addressPredictions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedAddressIndex((prev) =>
          prev > 0 ? prev - 1 : addressPredictions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedAddressIndex >= 0) {
          handleAddressSelect(addressPredictions[selectedAddressIndex]);
        }
        break;
      case 'Escape':
        setAddressPredictions([]);
        setShowAddressPredictions(false);
        setSelectedAddressIndex(-1);
        break;
      default:
        break;
    }
  };

  // Close predictions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        businessPredictionsRef.current &&
        !businessPredictionsRef.current.contains(event.target) &&
        businessSearchRef.current &&
        !businessSearchRef.current.contains(event.target)
      ) {
        setShowBusinessPredictions(false);
      }

      if (
        addressPredictionsRef.current &&
        !addressPredictionsRef.current.contains(event.target) &&
        address1Ref.current &&
        !address1Ref.current.contains(event.target)
      ) {
        setShowAddressPredictions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length === 0) return '';
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6)
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const handlePhoneChange = (value) => {
    const formatted = formatPhoneNumber(value);
    handleInputChange('phone', formatted);
  };

  const selectPreviousRecipient = (recipientData) => {
    onRecipientChange({
      ...recipient,
      firstName: recipientData.firstName,
      lastName: recipientData.lastName,
      businessName: recipientData.businessName || '',
      address1: recipientData.address1,
      address2: recipientData.address2 || '',
      city: recipientData.city,
      state: recipientData.state,
      zip: recipientData.zip,
      phone: formatPhoneNumber(recipientData.phone || '') // Format phone number for display
    });
    onNotification?.('Recipient information auto-filled from previous order', 'success', 3000);
  };

  const clearSection = () => {
    onRecipientChange({
      firstName: '',
      lastName: '',
      businessName: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
      phone: '',
    });
    setAddressValidation({});
    onNotification?.('Recipient information cleared', 'info', 2000);
  };

  const getFieldError = (field) => validationErrors[`recipient.${field}`];

  // ── Forest layout (New order, Recipient step): the design's 6-column grid ──
  if (layout === 'forest') {
    const field = (error, extra = '') => `input-field sm:text-base${error ? ' input-error' : ''}${extra ? ` ${extra}` : ''}`;
    const optional = <span className='field-hint font-normal text-gray-500'>· optional</span>;
    const required = <span className='input-required'>*</span>;
    const zipLooksWrong = recipient.zip && !validateZipCode(recipient.zip) && !getFieldError('zip');

    return (
      <div className='flex flex-col gap-3.5'>
        {/* Previous recipients — one card per recipient; click fills the recipient.
            Arrangement + price for reference (flowers rotate seasonally). */}
        {previousOrders.length > 0 && (
          <div className='rounded-md border border-gray-200 bg-gray-50 p-3'>
            <div className='mb-2.5 flex items-center justify-between gap-3'>
              <h3 className='text-[13px] font-semibold text-gray-900'>From their previous orders</h3>
              <span className='field-hint text-xs text-gray-500'>Click one to fill the recipient</span>
            </div>
            <div className='grid max-h-72 gap-2 overflow-y-auto' style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(175px, 1fr))' }}>
              {previousOrders.map((o, index) => (
                <button
                  key={index}
                  type='button'
                  onClick={() => selectPreviousRecipient(o)}
                  className='flex flex-col rounded-md border border-gray-200 bg-white p-3 text-left transition-colors duration-[120ms] hover:border-floral-700 hover:bg-floral-50'
                >
                  <span className='flex flex-wrap items-center gap-1.5 text-sm font-medium text-gray-900'>
                    {o.firstName} {o.lastName}
                    {o.isHana && (
                      <span className='inline-flex h-4 items-center rounded bg-neutral-bg px-1.5 text-[10px] font-semibold text-neutral-fg'>Hana</span>
                    )}
                  </span>
                  <span className='field-hint mt-1 text-xs leading-snug text-gray-600'>
                    {o.address1}{o.city ? `, ${o.city}` : ''} {o.state} {o.zip}
                  </span>
                  {o.arrangement && (
                    <span className='mt-1 text-xs leading-snug text-gray-500' style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{o.arrangement}</span>
                  )}
                  <span className='mt-2 flex items-end justify-between border-t border-gray-200 pt-2'>
                    <span>
                      <span className='block text-[10px] uppercase tracking-wide text-gray-500'>price point</span>
                      <span className='text-[15px] font-semibold tabular-nums text-gray-900'>${o.total.toFixed(2)}</span>
                    </span>
                    <span className='text-right text-[11px] text-gray-500'>{o.fulfillment}<br />{o.date}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {loadingRecipients && (
          <p className='field-hint text-[13px] text-gray-500'>Loading previous orders…</p>
        )}

        <div className='grid grid-cols-1 gap-x-4 gap-y-3.5 md:grid-cols-6'>
          <div className='md:col-span-3'>
            <label className='field-label input-label' htmlFor='recipient-first-name'>First name {required}</label>
            <input
              id='recipient-first-name'
              type='text'
              value={recipient.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              data-field='recipient-firstName'
              className={field(getFieldError('firstName'))}
              required
              tabIndex={tabIndex}
            />
            {getFieldError('firstName') && <p className='input-error-text'>{getFieldError('firstName')}</p>}
          </div>
          <div className='md:col-span-3'>
            <label className='field-label input-label' htmlFor='recipient-last-name'>Last name {required}</label>
            <input
              id='recipient-last-name'
              type='text'
              value={recipient.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              data-field='recipient-lastName'
              className={field(getFieldError('lastName'))}
              required
              tabIndex={tabIndex + 1}
            />
            {getFieldError('lastName') && <p className='input-error-text'>{getFieldError('lastName')}</p>}
          </div>

          {/* Business search fills the address (and the business name and phone) */}
          <div className='relative md:col-span-3'>
            <label className='field-label input-label' htmlFor='recipient-business-search'>Find a business {optional}</label>
            <div className='relative'>
              <SearchIcon size={16} strokeWidth={2} className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500' />
              <input
                id='recipient-business-search'
                ref={businessSearchRef}
                type='text'
                value={businessSearch}
                onChange={(e) => handleBusinessSearch(e.target.value)}
                onKeyDown={handleBusinessKeyDown}
                className={field(false, 'pl-9')}
                placeholder='Hospital, church, venue, shop…'
                tabIndex={tabIndex + 2}
                autoComplete='off'
              />
            </div>
            {showBusinessPredictions && businessPredictions.length > 0 && (
              <div
                ref={businessPredictionsRef}
                className='absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg'
              >
                <div className='border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-600'>
                  Business locations
                </div>
                {businessPredictions.map((prediction, index) => (
                  <div
                    key={prediction.place_id}
                    onClick={() => handleBusinessSelect(prediction)}
                    className={`cursor-pointer border-b border-gray-100 px-3 py-2.5 last:border-b-0 hover:bg-floral-50 ${
                      index === selectedBusinessIndex ? 'bg-floral-50' : ''
                    }`}
                  >
                    <div className='font-medium text-gray-900'>{prediction.structured_formatting.main_text}</div>
                    <div className='flex items-center gap-1 text-sm text-gray-600'>
                      <MapPinIcon size={12} className='shrink-0' />
                      {prediction.structured_formatting.secondary_text}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className='md:col-span-3'>
            <label className='field-label input-label' htmlFor='recipient-business-name'>Business name {optional}</label>
            <input
              id='recipient-business-name'
              type='text'
              value={recipient.businessName || ''}
              onChange={(e) => handleInputChange('businessName', e.target.value)}
              className={field(false)}
              tabIndex={tabIndex + 3}
            />
          </div>

          {/* Street address — Google Places address autocomplete */}
          <div className='relative md:col-span-6'>
            <label className='field-label input-label' htmlFor='recipient-address1'>Street address {required}</label>
            <div className='relative'>
              <SearchIcon size={16} strokeWidth={2} className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500' />
              <input
                id='recipient-address1'
                ref={address1Ref}
                type='text'
                value={recipient.address1}
                onChange={(e) => handleAddressChange(e.target.value)}
                onKeyDown={handleAddressKeyDown}
                data-field='recipient-address1'
                className={field(getFieldError('address1'), 'pl-9')}
                placeholder='123 Main Street'
                required
                tabIndex={tabIndex + 4}
                autoComplete='off'
              />
            </div>
            {getFieldError('address1') ? (
              <p className='input-error-text'>{getFieldError('address1')}</p>
            ) : (
              <p className='field-hint input-help'>Pick a suggestion to fill the city, state and ZIP.</p>
            )}
            {showAddressPredictions && addressPredictions.length > 0 && (
              <div
                ref={addressPredictionsRef}
                className='absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg'
              >
                <div className='border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-600'>
                  Suggested addresses ({addressPredictions.length})
                </div>
                {addressPredictions.map((prediction, index) => (
                  <div
                    key={prediction.place_id}
                    onClick={() => handleAddressSelect(prediction)}
                    className={`cursor-pointer border-b border-gray-100 px-3 py-2.5 last:border-b-0 hover:bg-floral-50 ${
                      index === selectedAddressIndex ? 'bg-floral-50' : ''
                    }`}
                  >
                    <div className='font-medium text-gray-900'>{prediction.structured_formatting.main_text}</div>
                    <div className='field-hint text-sm text-gray-600'>{prediction.structured_formatting.secondary_text}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className='md:col-span-2'>
            <label className='field-label input-label' htmlFor='recipient-address2'>Apt / suite {optional}</label>
            <input
              id='recipient-address2'
              type='text'
              value={recipient.address2}
              onChange={(e) => handleInputChange('address2', e.target.value)}
              className={field(false)}
              placeholder='Apt 4B'
              tabIndex={tabIndex + 4}
            />
            {clearedAddress2 && !recipient.address2 && (
              <p className='input-help'>
                Cleared &ldquo;{clearedAddress2}&rdquo; from the previous address.{' '}
                <button
                  type='button'
                  onClick={() => { handleInputChange('address2', clearedAddress2); }}
                  className='font-medium text-floral-700 underline hover:text-floral-800'
                >
                  Keep it
                </button>
              </p>
            )}
          </div>
          <div className='md:col-span-2'>
            <label className='field-label input-label' htmlFor='recipient-city'>City {required}</label>
            <input
              id='recipient-city'
              type='text'
              value={recipient.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              data-field='recipient-city'
              className={field(getFieldError('city'))}
              required
              tabIndex={tabIndex + 5}
            />
            {getFieldError('city') && <p className='input-error-text'>{getFieldError('city')}</p>}
          </div>
          <div className='md:col-span-1'>
            <label className='field-label input-label' htmlFor='recipient-state'>State {required}</label>
            <select
              id='recipient-state'
              name='recipient.state'
              value={recipient.state || ''}
              onChange={(e) => handleInputChange('state', e.target.value)}
              data-field='recipient-state'
              className={field(getFieldError('state'), '!pr-7 py-0')}
              required
              tabIndex={tabIndex + 6}
            >
              {/* A stored value that is not a two-letter code (an old
                  "South Carolina") still shows as itself. */}
              {recipient.state && !US_STATES.some((s) => s.code === recipient.state) && (
                <option value={recipient.state}>{recipient.state}</option>
              )}
              {US_STATES.map((s) => (
                <option key={s.code || 'none'} value={s.code} title={s.name}>{s.code || '—'}</option>
              ))}
            </select>
            {getFieldError('state') && <p className='input-error-text'>{getFieldError('state')}</p>}
          </div>
          <div className='md:col-span-1'>
            <label className='field-label input-label' htmlFor='recipient-zip'>ZIP {required}</label>
            <input
              id='recipient-zip'
              type='text'
              value={recipient.zip}
              onChange={(e) => handleInputChange('zip', e.target.value)}
              data-field='recipient-zip'
              className={field(getFieldError('zip') || zipLooksWrong, 'tabular-nums')}
              pattern='\d{5}(-\d{4})?'
              maxLength='10'
              required
              tabIndex={tabIndex + 7}
            />
            {getFieldError('zip') && <p className='input-error-text'>{getFieldError('zip')}</p>}
          </div>
          {zipLooksWrong && (
            <p className='input-error-text -mt-2 md:col-span-6 md:text-right'>
              Please enter a valid ZIP code (12345 or 12345-6789)
            </p>
          )}

          <div className='md:col-span-3'>
            {/* Required (this or an alternative number), marked like the names (QA B P2-1) */}
            <label className='field-label input-label' htmlFor='recipient-phone'>Recipient phone {required}</label>
            <input
              id='recipient-phone'
              type='tel'
              value={recipient.phone}
              onChange={(e) => handlePhoneChange(e.target.value)}
              data-field='recipient-phone'
              className={field(getFieldError('phone'), 'tabular-nums')}
              placeholder='(555) 123-4567'
              maxLength='14'
              tabIndex={tabIndex + 8}
            />
            <div className='mt-1.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-1'>
              {getFieldError('phone') ? (
                <p className='text-xs text-danger-fg'>{getFieldError('phone')}</p>
              ) : (
                <p className='field-hint text-xs text-gray-500'>Helpful for delivery coordination</p>
              )}
              {!recipient.alternativePhone && recipient.alternativePhone !== '' && (
                <button
                  type='button'
                  onClick={() => handleInputChange('alternativePhone', '')}
                  className='text-xs font-medium text-floral-700 hover:text-floral-800'
                  tabIndex={tabIndex + 9}
                >
                  + Alternative phone
                </button>
              )}
            </div>
          </div>
          {onDeliveryInstructionsChange && (
            <div className='md:col-span-3'>
              <label className='field-label input-label' htmlFor='recipient-delivery-instructions'>Delivery instructions {optional}</label>
              <input
                id='recipient-delivery-instructions'
                type='text'
                value={deliveryInstructions}
                onChange={(e) => onDeliveryInstructionsChange(e.target.value)}
                className={field(false)}
                placeholder='Ring doorbell, leave with receptionist, floor…'
                tabIndex={tabIndex + 9}
              />
            </div>
          )}

          {/* Alternative phone number (conditional) */}
          {recipient.alternativePhone !== undefined && (
            <>
              <div className='md:col-span-3'>
                <div className='flex items-center justify-between'>
                  <label className='field-label input-label' htmlFor='recipient-alt-phone'>Alternative phone</label>
                  <button
                    type='button'
                    onClick={() => onRecipientChange({ ...recipient, alternativePhone: undefined, alternativePhoneContext: undefined })}
                    className='mb-1.5 text-xs font-medium text-gray-500 hover:text-danger-fg'
                    tabIndex={tabIndex + 11}
                  >
                    Remove
                  </button>
                </div>
                <input
                  id='recipient-alt-phone'
                  type='tel'
                  value={recipient.alternativePhone || ''}
                  onChange={(e) => {
                    const formatted = formatPhoneNumber(e.target.value);
                    handleInputChange('alternativePhone', formatted);
                  }}
                  className={field(getFieldError('alternativePhone'), 'tabular-nums')}
                  placeholder='(555) 987-6543'
                  maxLength='14'
                  tabIndex={tabIndex + 10}
                />
                {getFieldError('alternativePhone') && <p className='input-error-text'>{getFieldError('alternativePhone')}</p>}
              </div>
              <div className='md:col-span-3'>
                <label className='field-label input-label' htmlFor='recipient-alt-context'>Context {optional}</label>
                <input
                  id='recipient-alt-context'
                  type='text'
                  value={recipient.alternativePhoneContext || ''}
                  onChange={(e) => handleInputChange('alternativePhoneContext', e.target.value)}
                  className={field(false)}
                  placeholder='e.g., cell phone, husband, business'
                  maxLength='50'
                  tabIndex={tabIndex + 11}
                />
                <p className='field-hint input-help'>Who to contact or when to use</p>
              </div>
            </>
          )}
        </div>

        {/* Address warnings */}
        {addressValidation.warnings && addressValidation.warnings.length > 0 && (
          <div className='banner banner-warn items-start font-normal'>
            <AlertTriangleIcon size={16} className='mt-px shrink-0' />
            <div>
              <h4 className='font-semibold'>Address warnings</h4>
              <ul className='mt-0.5 space-y-0.5'>
                {addressValidation.warnings.map((warning, index) => (
                  <li key={index}>• {warning}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Same as Customer Info */}
      {sameAsCustomer && (
        <div className='bg-floral-50 border border-floral-200 rounded-md p-3'>
          <div className='flex items-center'>
            <svg
              className='w-4 h-4 text-floral-700 mr-2'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
              />
            </svg>
            <p className='text-sm text-floral-800'>
              Recipient name and phone auto-filled from customer information
            </p>
          </div>
        </div>
      )}

      {/* Previous orders — one card per recipient; click fills the recipient. Arrangement + price shown for reference (flowers rotate seasonally). */}
      {previousOrders.length > 0 && (
        <div className='bg-floral-50 border border-floral-200 rounded-lg p-4'>
          <div className='flex items-center justify-between mb-3'>
            <h4 className='text-sm font-semibold text-floral-800 flex items-center'>
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Previous orders
            </h4>
            <span className='field-hint text-xs text-floral-600'>click to fill recipient</span>
          </div>
          <div className='grid gap-2 max-h-72 overflow-y-auto' style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(175px, 1fr))' }}>
            {previousOrders.map((o, index) => (
              <button
                key={index}
                type='button'
                onClick={() => selectPreviousRecipient(o)}
                className='flex flex-col text-left bg-white border-2 border-floral-300 rounded-lg p-3 hover:border-floral-500 hover:bg-floral-50 hover:shadow-md transition-all'
              >
                <span className='font-medium text-floral-800 text-sm'>
                  {o.firstName} {o.lastName}
                  {o.isHana && (
                    <span className='ml-1 font-medium px-1.5 py-0.5 rounded bg-[#FAEEDA] text-[#854F0B] [html[data-scheme=dark]:not([data-appearance=fable])_&]:bg-warn-bg [html[data-scheme=dark]:not([data-appearance=fable])_&]:text-warn-fg' style={{ fontSize: '10px' }}>Hana</span>
                  )}
                </span>
                <span className='field-hint text-xs text-floral-600 mt-1 leading-snug'>
                  {o.address1}{o.city ? `, ${o.city}` : ''} {o.state} {o.zip}
                </span>
                {o.arrangement && (
                  <span className='text-xs text-gray-500 mt-1 leading-snug' style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{o.arrangement}</span>
                )}
                <span className='mt-2 pt-2 border-t border-floral-100 flex items-end justify-between'>
                  <span>
                    <span className='block uppercase tracking-wide text-gray-400' style={{ fontSize: '10px' }}>price point</span>
                    <span className='text-base font-semibold text-floral-800'>${o.total.toFixed(2)}</span>
                  </span>
                  <span className='text-gray-400 text-right' style={{ fontSize: '11px' }}>{o.fulfillment}<br />{o.date}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {loadingRecipients && (
        <div className='flex items-center justify-center py-3 bg-floral-50 border border-floral-200 rounded-lg'>
          <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-floral-600 mr-2'></div>
          <span className='field-hint text-sm text-floral-600'>Loading previous orders...</span>
        </div>
      )}

      {/* Recipient Information */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        {/* First Name */}
        <div>
          <label className='field-label block text-sm font-medium text-floral-700 mb-2'>
            Recipient First Name *
          </label>
          <input
            type='text'
            value={recipient.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            data-field='recipient-firstName'
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-floral-500 ${
              getFieldError('firstName')
                ? 'border-red-300 focus:border-red-500'
                : 'border-floral-300 focus:border-floral-500'
            }`}
            placeholder='Enter first name'
            required
            tabIndex={tabIndex}
          />
          {getFieldError('firstName') && (
            <p className='mt-1 text-sm text-red-600'>
              {getFieldError('firstName')}
            </p>
          )}
        </div>

        {/* Last Name */}
        <div>
          <label className='field-label block text-sm font-medium text-floral-700 mb-2'>
            Recipient Last Name *
          </label>
          <input
            type='text'
            value={recipient.lastName}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            data-field='recipient-lastName'
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-floral-500 ${
              getFieldError('lastName')
                ? 'border-red-300 focus:border-red-500'
                : 'border-floral-300 focus:border-floral-500'
            }`}
            placeholder='Enter last name'
            required
            tabIndex={tabIndex + 1}
          />
          {getFieldError('lastName') && (
            <p className='mt-1 text-sm text-red-600'>
              {getFieldError('lastName')}
            </p>
          )}
        </div>
      </div>

      {/* Business Name (Optional) - Only show for delivery */}
      {fulfillmentType === 'delivery' && (
        <div>
          <label className='field-label block text-sm font-medium text-floral-700 mb-2'>
            Business Name{' '}
            <span className='field-hint text-floral-500 text-xs'>(optional)</span>
          </label>
          <input
            type='text'
            value={recipient.businessName || ''}
            onChange={(e) => handleInputChange('businessName', e.target.value)}
            className='w-full px-3 py-2 border border-floral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-floral-500 focus:border-floral-500'
            placeholder='Enter business name if applicable'
            tabIndex={tabIndex + 2}
          />
        </div>
      )}

      {/* Address Fields - Only for Delivery */}
      {fulfillmentType === 'delivery' && (
        <div className='space-y-4'>
          {/* Business Search Field */}
          <div className='relative'>
            <label className='field-label block text-sm font-medium text-floral-700 mb-2'>
              Business Search{' '}
              <span className='field-hint text-floral-500 text-xs'>
                (optional - search for businesses like "Starbucks", "Hospital")
              </span>
            </label>
            <input
              ref={businessSearchRef}
              type='text'
              value={businessSearch}
              onChange={(e) => handleBusinessSearch(e.target.value)}
              onKeyDown={handleBusinessKeyDown}
              className='w-full px-3 py-2 border border-floral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-floral-500 focus:border-floral-500 bg-floral-50'
              placeholder="Search for businesses (Walmart, McDonald's, etc.)"
              tabIndex={tabIndex + 3}
              autoComplete='off'
            />
            <p className='field-hint mt-1 text-xs text-floral-700'>
              Search for businesses to auto-fill the address below
            </p>

            {/* Business Predictions Dropdown */}
            {showBusinessPredictions && businessPredictions.length > 0 && (
              <div
                ref={businessPredictionsRef}
                className='absolute z-30 w-full mt-1 bg-white border border-floral-200 rounded-md shadow-lg max-h-60 overflow-auto'
              >
                <div className='py-1'>
                  <div className='px-3 py-2 text-xs font-medium text-floral-700 bg-floral-50 border-b border-floral-100'>
                    Business Locations
                  </div>
                  {businessPredictions.map((prediction, index) => (
                    <div
                      key={prediction.place_id}
                      onClick={() => handleBusinessSelect(prediction)}
                      className={`px-3 py-3 cursor-pointer border-b border-floral-50 last:border-b-0 hover:bg-floral-50 ${
                        index === selectedBusinessIndex ? 'bg-floral-100' : ''
                      }`}
                    >
                      <div className='font-medium text-floral-800'>
                        {prediction.structured_formatting.main_text}
                      </div>
                      <div className='text-sm text-floral-700'>
                        {prediction.structured_formatting.secondary_text}
                      </div>
                      <div className='field-hint text-xs text-floral-700 mt-1 flex items-center gap-1'>
                        <MapPinIcon size={12} />
                        Business Location
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Address Line 1 - Optimized for street addresses */}
          <div className='relative'>
            <label className='field-label block text-sm font-medium text-floral-700 mb-2'>
              Street Address *
            </label>
            <input
              ref={address1Ref}
              type='text'
              value={recipient.address1}
              onChange={(e) => handleAddressChange(e.target.value)}
              onKeyDown={handleAddressKeyDown}
              data-field='recipient-address1'
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-floral-500 ${
                getFieldError('address1')
                  ? 'border-red-300 focus:border-red-500'
                  : 'border-floral-300 focus:border-floral-500'
              }`}
              placeholder='123 Main Street'
              required
              tabIndex={tabIndex + 4}
              autoComplete='off'
            />
            {getFieldError('address1') && (
              <p className='mt-1 text-sm text-red-600'>
                {getFieldError('address1')}
              </p>
            )}

            {/* Address Autocomplete Dropdown */}
            {showAddressPredictions && addressPredictions.length > 0 && (
              <div
                ref={addressPredictionsRef}
                className='absolute z-20 w-full mt-1 bg-white border border-floral-200 rounded-md shadow-lg max-h-60 overflow-auto'
              >
                <div className='py-1'>
                  <div className='px-3 py-2 text-xs font-medium text-floral-600 bg-floral-50 border-b border-floral-100'>
                    Suggested Addresses ({addressPredictions.length})
                  </div>
                  {addressPredictions.map((prediction, index) => (
                    <div
                      key={prediction.place_id}
                      onClick={() => handleAddressSelect(prediction)}
                      className={`px-3 py-3 cursor-pointer border-b border-floral-50 last:border-b-0 hover:bg-floral-50 ${
                        index === selectedAddressIndex ? 'bg-floral-100' : ''
                      }`}
                    >
                      <div className='font-medium text-floral-800'>
                        {prediction.structured_formatting.main_text}
                      </div>
                      <div className='field-hint text-sm text-floral-600'>
                        {prediction.structured_formatting.secondary_text}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Address Line 2 */}
          <div>
            <label className='field-label block text-sm font-medium text-floral-700 mb-2'>
              Apartment, Suite, Unit{' '}
              <span className='field-hint text-floral-500 text-xs'>(optional)</span>
            </label>
            <input
              type='text'
              value={recipient.address2}
              onChange={(e) => handleInputChange('address2', e.target.value)}
              className='w-full px-3 py-2 border border-floral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-floral-500 focus:border-floral-500'
              placeholder='Apt 4B, Suite 100, etc.'
              tabIndex={tabIndex + 4}
            />
            {clearedAddress2 && !recipient.address2 && (
              <p className='mt-1 text-sm text-floral-700'>
                Cleared &ldquo;{clearedAddress2}&rdquo; from the previous address.{' '}
                <button
                  type='button'
                  onClick={() => { handleInputChange('address2', clearedAddress2); }}
                  className='font-medium underline text-floral-800 hover:text-floral-900'
                >
                  Keep it
                </button>
              </p>
            )}
            {isEditing && strayRoomInAddress2(recipient.address2, locationType) && (
              <div className='mt-2 flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-300 rounded-md text-sm text-amber-800'>
                <AlertTriangleIcon size={16} className='mt-0.5 flex-shrink-0 text-amber-600' />
                <span>
                  Address 2 still says &ldquo;{recipient.address2}&rdquo;, but this isn&rsquo;t a hospital delivery.{' '}
                  <button
                    type='button'
                    onClick={() => handleInputChange('address2', '')}
                    className='font-medium underline hover:text-amber-900'
                  >
                    Clear it
                  </button>
                </span>
              </div>
            )}
          </div>

          {/* City, State, ZIP */}
          <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
            {/* City */}
            <div>
              <label className='field-label block text-sm font-medium text-floral-700 mb-2'>
                City *
              </label>
              <input
                type='text'
                value={recipient.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                data-field='recipient-city'
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-floral-500 ${
                  getFieldError('city')
                    ? 'border-red-300 focus:border-red-500'
                    : 'border-floral-300 focus:border-floral-500'
                }`}
                placeholder='Enter city'
                required
                tabIndex={tabIndex + 5}
              />
              {getFieldError('city') && (
                <p className='mt-1 text-sm text-red-600'>
                  {getFieldError('city')}
                </p>
              )}
            </div>

            {/* State */}
            <div>
              <label className='field-label block text-sm font-medium text-floral-700 mb-2'>
                State *
              </label>
              <select
                name='recipient.state'
                value={recipient.state || ''}
                onChange={(e) => handleInputChange('state', e.target.value)}
                data-field='recipient-state'
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-floral-500 ${
                  getFieldError('state')
                    ? 'border-red-300 focus:border-red-500'
                    : 'border-floral-300 focus:border-floral-500'
                }`}
                required
                tabIndex={tabIndex + 6}
              >
                {/* A stored value that is not a two-letter code (an old
                    "South Carolina") still shows as itself. */}
                {recipient.state && !US_STATES.some((s) => s.code === recipient.state) && (
                  <option value={recipient.state}>{recipient.state}</option>
                )}
                {US_STATES.map((s) => (
                  <option key={s.code || 'none'} value={s.code}>{s.name}</option>
                ))}
              </select>
              {getFieldError('state') && (
                <p className='mt-1 text-sm text-red-600'>
                  {getFieldError('state')}
                </p>
              )}
            </div>

            {/* ZIP Code */}
            <div>
              <label className='field-label block text-sm font-medium text-floral-700 mb-2'>
                ZIP Code *
              </label>
              <input
                type='text'
                value={recipient.zip}
                onChange={(e) => handleInputChange('zip', e.target.value)}
                data-field='recipient-zip'
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-floral-500 ${
                  getFieldError('zip')
                    ? 'border-red-300 focus:border-red-500'
                    : recipient.zip && !validateZipCode(recipient.zip)
                      ? 'border-yellow-300 focus:border-yellow-500'
                      : 'border-floral-300 focus:border-floral-500'
                }`}
                placeholder='12345 or 12345-6789'
                pattern='\d{5}(-\d{4})?'
                maxLength='10'
                required
                tabIndex={tabIndex + 7}
              />
              {getFieldError('zip') && (
                <p className='mt-1 text-sm text-red-600'>
                  {getFieldError('zip')}
                </p>
              )}
              {recipient.zip &&
                !validateZipCode(recipient.zip) &&
                !getFieldError('zip') && (
                  <p className='mt-1 text-sm text-yellow-600'>
                    Please enter a valid ZIP code (12345 or 12345-6789)
                  </p>
                )}
              {recipient.zip && validateZipCode(recipient.zip) && (
                <p className='field-hint mt-1 text-sm text-floral-600'>✓ Valid ZIP code</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Phone Number */}
      <div>
        {/* Required (this or an alternative number), marked like the names (QA B P2-1) */}
        <label className='field-label block text-sm font-medium text-floral-700 mb-2'>
          Recipient Phone Number *
        </label>
        <input
          type='tel'
          value={recipient.phone}
          onChange={(e) => handlePhoneChange(e.target.value)}
          data-field='recipient-phone'
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-floral-500 ${
            getFieldError('phone')
              ? 'border-red-300 focus:border-red-500'
              : 'border-floral-300 focus:border-floral-500'
          }`}
          placeholder='(555) 123-4567'
          maxLength='14'
          tabIndex={tabIndex + 8}
        />
        <div className='flex items-center justify-between mt-1'>
          {getFieldError('phone') ? (
            <p className='text-sm text-red-600'>{getFieldError('phone')}</p>
          ) : (
            <p className='field-hint text-xs text-floral-500'>
              Helpful for delivery coordination
            </p>
          )}
          {!recipient.alternativePhone && (
            <button
              type='button'
              onClick={() => handleInputChange('alternativePhone', '')}
              className='text-xs text-floral-600 hover:text-floral-800 flex items-center'
              tabIndex={tabIndex + 9}
            >
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              Add alternative phone
            </button>
          )}
        </div>
      </div>

      {/* Alternative Phone Number (Conditional) */}
      {recipient.alternativePhone !== undefined && (
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4 bg-floral-50 p-4 rounded-lg border border-floral-200'>
          <div>
            <div className='flex items-center justify-between mb-2'>
              <label className='field-label block text-sm font-medium text-floral-700'>
                Alternative Phone Number
              </label>
              <button
                type='button'
                onClick={() => {
                  const updated = { ...recipient };
                  delete updated.alternativePhone;
                  delete updated.alternativePhoneContext;
                  handleInputChange('alternativePhone', undefined);
                  handleInputChange('alternativePhoneContext', undefined);
                }}
                className='text-xs text-red-600 hover:text-red-800'
                tabIndex={tabIndex + 11}
              >
                Remove
              </button>
            </div>
            <input
              type='tel'
              value={recipient.alternativePhone || ''}
              onChange={(e) => {
                const formatted = formatPhoneNumber(e.target.value);
                handleInputChange('alternativePhone', formatted);
              }}
              className='w-full px-3 py-2 border border-floral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-floral-500 focus:border-floral-500'
              placeholder='(555) 987-6543'
              maxLength='14'
              tabIndex={tabIndex + 10}
            />
          </div>
          <div>
            <label className='field-label block text-sm font-medium text-floral-700 mb-2'>
              Context <span className='field-hint text-floral-500 text-xs'>(optional)</span>
            </label>
            <input
              type='text'
              value={recipient.alternativePhoneContext || ''}
              onChange={(e) => handleInputChange('alternativePhoneContext', e.target.value)}
              className='w-full px-3 py-2 border border-floral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-floral-500 focus:border-floral-500'
              placeholder='e.g., cell phone, husband, business'
              maxLength='50'
              tabIndex={tabIndex + 11}
            />
            <p className='field-hint mt-1 text-xs text-floral-500'>
              Who to contact or when to use
            </p>
          </div>
        </div>
      )}

      {/* Address Validation Warnings */}
      {addressValidation.warnings && addressValidation.warnings.length > 0 && (
        <div className='bg-yellow-50 border border-yellow-200 rounded-md p-4'>
          <div className='flex items-start'>
            <svg
              className='w-5 h-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L5.082 15.5c-.77.833.192 2.5 1.732 2.5z'
              />
            </svg>
            <div className='flex-1'>
              <h4 className='text-sm font-medium text-yellow-800'>
                Address Warnings
              </h4>
              <ul className='mt-1 text-sm text-yellow-700 space-y-1'>
                {addressValidation.warnings.map((warning, index) => (
                  <li key={index}>• {warning}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecipientSection;
